import { SelectionStateModel, SelectedState } from '../generic-table/TableControls';

export class TableStateManagement<T> {
  private readonly _selected = new Map<string | number, T>();

  constructor(
    private readonly identityFn: (item: T) => string | number,
    private readonly mode: 'single' | 'multi',
  ) {}

  toggle(item: T): void {
    const key = this.identityFn(item);
    if (this.mode === 'single') {
      this._selected.clear();
      this._selected.set(key, item);
    } else {
      if (this._selected.has(key)) {
        this._selected.delete(key);
      } else {
        this._selected.set(key, item);
      }
    }
  }

  select(item: T): void {
    this._selected.set(this.identityFn(item), item);
  }

  deselect(item: T): void {
    this._selected.delete(this.identityFn(item));
  }

  isSelected(item: T): boolean {
    return this._selected.has(this.identityFn(item));
  }

  toggleAll(items: T[], checked: boolean): void {
    if (checked) {
      items.forEach(item => this._selected.set(this.identityFn(item), item));
    } else {
      items.forEach(item => this._selected.delete(this.identityFn(item)));
    }
  }

  isAllSelected(items: T[]): boolean {
    return items.length > 0 && items.every(item => this._selected.has(this.identityFn(item)));
  }

  isIndeterminate(items: T[]): boolean {
    const selectedCount = items.filter(item => this._selected.has(this.identityFn(item))).length;
    return selectedCount > 0 && selectedCount < items.length;
  }

  handleItemSelectionChange(model: SelectionStateModel<T>): void {
    if (model.state === SelectedState.SELECTED) {
      this.select(model.item);
    } else {
      this.deselect(model.item);
    }
  }

  getSelected(): T[] {
    return [...this._selected.values()];
  }

  clearAll(): void {
    this._selected.clear();
  }

  count(): number {
    return this._selected.size;
  }
}
